import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Target, Sparkles, Check, X, Trash2, Loader2, Flag, ChevronDown, Compass } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import StrategicGoalWizard from "@/app/components/StrategicGoalWizard";
import LifecycleDraftModal from "@/app/components/LifecycleDraftModal";

const CATEGORIES = ["Revenue", "Customers", "Sales", "Marketing", "Product", "Operations", "Projects", "Team", "Partnerships", "Expansion", "Productivity", "Other"];
const STATUSES = [["active", "Active"], ["paused", "Paused"], ["completed", "Completed"], ["archived", "Archived"]];
const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";

export default function GoalsPage() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [planning, setPlanning] = useState(null); // goal_id being planned
  const [preview, setPreview] = useState(null); // {goal_id, stages}
  const [lifecycleGoal, setLifecycleGoal] = useState(null); // {id, name}
  const [form, setForm] = useState({ name: "", category: "Customers", target: "", target_unit: "", target_date: "", priority: "high", why_matters: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/goals");
      setGoals(data.goals || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load goals."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const { data } = await api.post("/goals", form);
      setGoals((g) => [data.goal, ...g]);
      setForm({ name: "", category: "Customers", target: "", target_unit: "", target_date: "", priority: "high", why_matters: "" });
      setShowAdd(false);
      toast.success("Goal added");
      setExpanded(data.goal.id);
      // Phase 12.7 P0 — offer AI roadmap draft immediately on creation (skip for strategic goals which use wizard)
      if (data.goal.goal_type !== "strategic") {
        setLifecycleGoal({ id: data.goal.id, name: data.goal.name });
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const patch = async (id, changes) => {
    try {
      const { data } = await api.patch(`/goals/${id}`, changes);
      setGoals((all) => all.map((g) => (g.id === id ? data.goal : g)));
    } catch { toast.error("Could not update goal."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this goal and its plan?")) return;
    try { await api.delete(`/goals/${id}`); setGoals((g) => g.filter((x) => x.id !== id)); } catch { toast.error("Could not delete."); }
  };

  const generatePlan = async (id) => {
    setPlanning(id);
    try {
      const { data } = await api.post(`/goals/${id}/plan`);
      setPreview({ goal_id: id, stages: data.preview?.stages || [] });
    } catch { toast.error("AI plan generation failed."); }
    finally { setPlanning(null); }
  };

  const approvePlan = async () => {
    if (!preview) return;
    try {
      const { data } = await api.post(`/goals/${preview.goal_id}/approve-plan`, { stages: preview.stages });
      setGoals((all) => all.map((g) => (g.id === preview.goal_id ? data.goal : g)));
      setPreview(null);
      toast.success("Plan saved to your goal.");
    } catch { toast.error("Could not save plan."); }
  };

  const toggleMilestone = async (goalId, msId, done) => {
    try {
      const { data } = await api.patch(`/goals/${goalId}/milestone/${msId}?done=${done}`);
      setGoals((all) => all.map((g) => (g.id === goalId ? data.goal : g)));
    } catch { toast.error("Could not update milestone."); }
  };

  const activeGoals = useMemo(() => goals.filter((g) => g.status !== "archived" && g.status !== "completed"), [goals]);
  const otherGoals = useMemo(() => goals.filter((g) => g.status === "completed" || g.status === "archived"), [goals]);
  const strategicPrimary = goals.filter((g) => g.goal_type === "strategic" && g.strategic_role === "primary" && g.status !== "archived").length;
  const strategicSupporting = goals.filter((g) => g.goal_type === "strategic" && g.strategic_role === "supporting" && g.status !== "archived").length;
  const canAddStrategic = strategicPrimary < 1 || strategicSupporting < 2;
  const [strategicOpen, setStrategicOpen] = useState(false);
  const strategicDefaultRole = strategicPrimary < 1 ? "primary" : "supporting";

  return (
    <div data-testid="goals-page">
      <Seo title="Goals" description="Business goals, stages and progress." path="/app/goals" />
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow text-brand-teal">Business Goals</p>
          <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight sm:text-3xl">What you're building toward.</h1>
          <p className="mt-1 text-sm text-muted-foreground">Give the AI real targets — it'll help you convert them into a plan you can act on.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canAddStrategic && (
            <button
              onClick={() => setStrategicOpen(true)}
              data-testid="strategic-goal-add"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-teal/40 bg-brand-teal/10 px-4 py-2 text-sm font-semibold text-brand-teal hover:bg-brand-teal/20"
            >
              <Compass className="h-4 w-4" /> Add strategic goal
            </button>
          )}
          <button onClick={() => setShowAdd((v) => !v)} data-testid="goal-add-open" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add goal</button>
        </div>
      </header>

      <StrategicGoalWizard
        open={strategicOpen}
        onOpenChange={setStrategicOpen}
        defaultRole={strategicDefaultRole}
        onCreated={(g) => { setGoals((prev) => [g, ...prev]); setStrategicOpen(false); }}
      />

      {showAdd && (
        <form onSubmit={create} className="mb-5 grid gap-3 rounded-2xl border border-border bg-card p-4 md:grid-cols-2" data-testid="goal-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Goal — e.g. Reach 100 paying customers" className={`${inputCls} md:col-span-2`} data-testid="goal-name-input" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="goal-category-select">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls} data-testid="goal-priority-select">
            <option value="low">Low priority</option><option value="medium">Medium priority</option><option value="high">High priority</option>
          </select>
          <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="Target — e.g. 100" className={inputCls} data-testid="goal-target-input" />
          <input value={form.target_unit} onChange={(e) => setForm({ ...form, target_unit: e.target.value })} placeholder="Unit — customers, MRR $, deals…" className={inputCls} data-testid="goal-unit-input" />
          <input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className={inputCls} data-testid="goal-date-input" />
          <textarea rows={2} value={form.why_matters} onChange={(e) => setForm({ ...form, why_matters: e.target.value })} placeholder="Why this matters (optional)" className={`${inputCls} md:col-span-2 resize-none`} data-testid="goal-why-input" />
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="rounded-full border border-border px-4 py-2 text-sm font-medium">Cancel</button>
            <button type="submit" data-testid="goal-add-submit" className="rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Save goal</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          {activeGoals.length === 0 && otherGoals.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No goals yet. Add your first goal — the AI will help you break it down into stages and milestones.
            </div>
          ) : (
            <div className="space-y-3">
              {activeGoals.map((g) => (
                <GoalCard key={g.id} goal={g} expanded={expanded === g.id} onExpand={() => setExpanded(expanded === g.id ? null : g.id)}
                  onGenerate={() => generatePlan(g.id)} planning={planning === g.id}
                  onPatch={(chg) => patch(g.id, chg)} onDelete={() => remove(g.id)}
                  onToggleMilestone={(mid, d) => toggleMilestone(g.id, mid, d)} />
              ))}
              {otherGoals.length > 0 && (
                <div className="pt-4">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Completed & archived</p>
                  <div className="space-y-3">
                    {otherGoals.map((g) => (
                      <GoalCard key={g.id} goal={g} expanded={expanded === g.id} onExpand={() => setExpanded(expanded === g.id ? null : g.id)}
                        onGenerate={() => generatePlan(g.id)} planning={planning === g.id}
                        onPatch={(chg) => patch(g.id, chg)} onDelete={() => remove(g.id)}
                        onToggleMilestone={(mid, d) => toggleMilestone(g.id, mid, d)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Plan preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm p-3 sm:items-center" data-testid="plan-preview">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md brand-gradient-bg text-white"><Sparkles className="h-4 w-4" /></span>
                <p className="font-display text-sm font-semibold">AI-proposed plan</p>
              </div>
              <button onClick={() => setPreview(null)} data-testid="plan-preview-close" className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-[65vh] space-y-3 overflow-y-auto p-5">
              {preview.stages.length === 0 ? (
                <p className="text-sm text-muted-foreground">The AI couldn't build a structured plan this time — try again in a moment.</p>
              ) : preview.stages.map((s, si) => (
                <div key={s.id || si} className="rounded-2xl border border-border bg-background/60 p-4">
                  <p className="font-display text-sm font-semibold">Stage {si + 1}: {s.name}</p>
                  {s.description && <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>}
                  <ul className="mt-2 space-y-1.5">
                    {(s.milestones || []).map((m, mi) => (
                      <li key={m.id || mi} className="flex items-start gap-2 text-xs">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-teal" />
                        <div>
                          <p className="font-medium">{m.name}</p>
                          {(m.measurable_target || m.due_hint) && <p className="text-muted-foreground">{[m.measurable_target, m.due_hint].filter(Boolean).join(" · ")}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
              <button onClick={() => generatePlan(preview.goal_id)} disabled={planning === preview.goal_id} data-testid="plan-regenerate" className="text-xs font-medium text-muted-foreground hover:text-foreground">Regenerate</button>
              <div className="flex gap-2">
                <button onClick={() => setPreview(null)} className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold">Discard</button>
                <button onClick={approvePlan} data-testid="plan-approve" disabled={preview.stages.length === 0} className="rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60">Approve plan</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <LifecycleDraftModal
        trigger="goal_created"
        entityId={lifecycleGoal?.id}
        entityLabel={lifecycleGoal?.name}
        open={!!lifecycleGoal}
        onClose={() => setLifecycleGoal(null)}
        onApproved={() => { load(); setLifecycleGoal(null); }}
      />
    </div>
  );
}

function GoalCard({ goal, expanded, onExpand, onGenerate, planning, onPatch, onDelete, onToggleMilestone }) {
  const total = (goal.stages || []).reduce((s, x) => s + (x.milestones?.length || 0), 0);
  const done = (goal.stages || []).reduce((s, x) => s + (x.milestones || []).filter((m) => m.done).length, 0);
  const currentStage = (goal.stages || []).find((s) => (s.milestones || []).some((m) => !m.done));
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`goal-card-${goal.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={onExpand} data-testid={`goal-expand-${goal.id}`} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${goal.goal_type === "strategic" ? "bg-brand-teal/15 text-brand-teal" : "bg-muted text-brand-teal"}`}>
              {goal.goal_type === "strategic" ? <Compass className="h-4 w-4" /> : <Target className="h-4 w-4" />}
            </span>
            <p className="truncate font-display text-base font-semibold">{goal.name}</p>
            {goal.goal_type === "strategic" && (
              <span className="rounded-full bg-brand-teal/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-brand-teal" data-testid={`strategic-label-${goal.id}`}>
                Strategic · {(goal.strategic_role || "supporting").toLowerCase()}
              </span>
            )}
            {goal.source_board_decision_id && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary" data-testid={`from-board-badge-goal-${goal.id}`} title="Created from a Board decision">
                <Sparkles className="h-2.5 w-2.5" /> From Board
              </span>
            )}
            <span className={`hidden rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 sm:inline ${goal.priority === "high" ? "bg-brand-red/10 text-brand-red ring-brand-red/30" : goal.priority === "medium" ? "bg-brand-orange/10 text-brand-orange ring-brand-orange/30" : "bg-muted text-muted-foreground ring-border"}`}><Flag className="mr-1 inline h-2.5 w-2.5" />{goal.priority}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span>{goal.category}</span>
            {goal.target && <span>Target: {goal.target} {goal.target_unit}</span>}
            {goal.target_date && <span>by {goal.target_date.slice(0, 10)}</span>}
            {currentStage && <span>· Current stage: <b className="text-foreground">{currentStage.name}</b></span>}
          </div>
        </button>
        <ChevronDown className={`mt-1 h-4 w-4 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
        <button onClick={onDelete} data-testid={`goal-delete-${goal.id}`} className="mt-1 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>

      <div className="mt-2 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-brand-teal transition-all" style={{ width: `${goal.progress || 0}%` }} data-testid={`goal-progress-${goal.id}`} />
        </div>
        <span className="text-[11px] font-semibold text-muted-foreground">{goal.progress || 0}%</span>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-border pt-3" data-testid={`goal-detail-${goal.id}`}>
          {goal.why_matters && <p className="text-xs text-muted-foreground italic">Why: {goal.why_matters}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <select value={goal.status} onChange={(e) => onPatch({ status: e.target.value })} data-testid={`goal-status-${goal.id}`} className="rounded-lg border border-border bg-background px-2 py-1 text-xs">
              {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={onGenerate} disabled={planning} data-testid={`goal-generate-plan-${goal.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-brand-teal hover:bg-muted disabled:opacity-60">
              {planning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {(goal.stages || []).length > 0 ? "Regenerate AI plan" : "Generate AI plan"}
            </button>
          </div>

          {(goal.stages || []).length > 0 ? (
            <div className="space-y-2">
              {goal.stages.map((s, si) => (
                <div key={s.id || si} className="rounded-xl border border-border bg-background/60 p-3">
                  <p className="text-xs font-semibold">Stage {si + 1}: {s.name}</p>
                  <ul className="mt-1.5 space-y-1">
                    {(s.milestones || []).map((m) => (
                      <li key={m.id} className="flex items-start gap-2">
                        <button onClick={() => onToggleMilestone(m.id, !m.done)} data-testid={`milestone-toggle-${m.id}`}
                          className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${m.done ? "border-brand-teal bg-brand-teal text-white" : "border-border"}`}>
                          {m.done && <Check className="h-3 w-3" />}
                        </button>
                        <div className="min-w-0">
                          <p className={`text-xs ${m.done ? "text-muted-foreground line-through" : ""}`}>{m.name}</p>
                          {(m.measurable_target || m.due_hint) && <p className="text-[10px] text-muted-foreground">{[m.measurable_target, m.due_hint].filter(Boolean).join(" · ")}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-xl bg-background/60 p-3 text-xs text-muted-foreground">No AI plan yet. Tap <b>Generate AI plan</b> to have Workspace break this goal into stages and measurable milestones tailored to your business.</p>
          )}
        </div>
      )}
    </div>
  );
}
